/**
 * @format
 * @module ui/modal-manager
 */
import extend from "lodash/extend";
import { registerLayer } from "panther/scroll-manager";
import ee from "lib/eventemitter";
import { toggleBgAccess, bootstrapFocusElements, getFirstFocusableElement } from "ui/focus-manager";
import { closeLightBox, openLightBox, closeModalCallback } from "ui/lightbox";
import { closeButton } from "ui/templates/btn";
import { focusOnElement } from "utils/dom-controller";
import logger from "utils/logger";
import { qsa } from "utils/utils";
import isNil from "lodash/isNil";

const debugTag = "[MODAL-MANAGER]";
const modalOuterClass = "o-modal-outer-container";
const closeButtonTemplate = closeButton({ classes: ["js-c-button-close-modal"] });
const modalRegister = {};

const defaultModalConfig = {
  uid: null,
  iframeUrl: null,
  sourceElement: "#js-big-mac",
  templates: null,
  width: "500",
  height: "640",
  topPaddingForCloseButton: false,
  protectedModal: false,
  scrollable: false,
  onOpenModal: null,
  onCloseModal: null,
  onCloseFunction: hideAllModals,
  lightboxClasses: []
};

let modalId = 0;
let templateIndex = 0;
let modalOuterContainer = null;

/**
 * @method configureModal
 * @description configures each instance of a modal and returns a function to be executed later
 * @param {Object} modalConfig - config for the modal
 * @returns {Function} to be executed at a later time
 */
export function configureModal(modalConfig) {
  const extendedConfig = extend({}, defaultModalConfig, modalConfig);
  logger.info(`${debugTag} configureModal():`, extendedConfig.uid, extendedConfig);

  let ret = null;
  if (isNil(extendedConfig.uid)) {
    ret = () =>
      logger.error(
        "modalConfig.uid is not defined, in order to open the modal a unique id must be defined."
      );
  } else if (!isUniqueId(extendedConfig.uid)) {
    ret = () =>
      logger.error(
        `${extendedConfig.uid} already exists in the modal register, please use a different unique id.`
      );
  } else {
    ret = url => {
      if (url) {
        extendedConfig.iframeUrl = url;
      }
      showModal(extendedConfig);
    };
  }

  return ret;
}

/**
 * @method showModal
 * @description creates a modal container element, based on the configuration it will either load an iframe or a markup
 * @param {Object} c - collection of properties
 * @param {String} [c.uid] - a unique id
 * @param {String} [c.iframeUrl] - a url value for the iframe
 * @param {Array} [c.templates] - an array of markup
 * @param {Function} [c.sourceElement] - the element to focus when the modal closes
 * @param {String} [c.width] - the width of the modal, can be a number as a string or auto, ie: "500" or "auto" (NOTE that auto should only be used when modal is configured to use a template instead of an iframe)
 * @param {String} [c.height] - the height of the modal, can be a number as a string or auto, ie: "640" or "auto" (NOTE that auto should only be used when modal is configured to use a template instead of an iframe)
 * @param {Boolean} [c.topPaddingForCloseButton] - whether to reserve roughly 50px at the top of the modal for a button, typically a close button
 * @param {Boolean} [c.protectedModal] - whether the boolean is closable when they hit the close button or click outside the modal
 * @param {Boolean} [c.scrollable] - whether the iframe within the modal is scrollable
 * @param {Function} [c.onOpenModal] - a method to call once the modal opens, the method is scoped to each instance of the modal
 * @param {Function} [c.onCloseModal] - a method to call once the modal closes, the method is scoped to each instance of the modal
 * @param {Function} [c.onCloseFunction] - the method to call when the lightbox bg is clicked
 * @param {Array} [c.lightboxClasses] - additional CSS classes to be added to the lightbox element (default is an empty array)
 * @returns {Boolean}
 */
function showModal({
  uid,
  iframeUrl,
  templates,
  sourceElement,
  width,
  height,
  topPaddingForCloseButton,
  protectedModal,
  scrollable,
  onOpenModal,
  onCloseModal,
  onCloseFunction,
  lightboxClasses
}) {
  logger.info(`${debugTag} showModal() | uid:`, uid);

  hideAllModals();

  registerLayer({
    key: uid,
    lockEvent: "modal:opened",
    unlockEvent: "modal:closed",
    saveScrollEvent: "modal:open",
    resetScrollEvent: "modal:closed",
    isOpen: () => !isNil(document.querySelector(`#${uid}.${modalOuterClass}--opened`))
  });

  const SMALL_MODAL_PADDING = 50 + (topPaddingForCloseButton ? 44 : 0) + 20; // Top margin, Close button padding, bottom margin
  const isViewShorter = window.innerHeight < height; // If the modal is taller than the view height
  const modalInner = document.createElement("div");

  let focusTarget = null;

  modalInner.id = uid;
  modalInner.classList.add(modalOuterClass);
  modalInner.classList.add(`${modalOuterClass}--opened`);
  modalInner.classList.add(`${modalOuterClass}--${uid}`);

  if (isViewShorter || topPaddingForCloseButton) {
    modalInner.classList.add(`${modalOuterClass}--top-padding`);
  }

  if (!isNil(iframeUrl)) {
    logger.info(`${debugTag} showModal() | iframe mode`);

    // adds close button
    modalInner.innerHTML = closeButtonTemplate;

    const iframe = document.createElement("iframe");
    // id, name, and class are used for Nightwatch tests
    iframe.id = uid + "-iframe";
    iframe.name = "modal-manager-iframe";
    iframe.className = "js-modal-manager-iframe";
    iframe.src = iframeUrl;
    iframe.width = width;
    iframe.height = isViewShorter && scrollable ? window.innerHeight - SMALL_MODAL_PADDING : height;
    iframe.scrolling = scrollable || isViewShorter ? "yes" : "no";
    iframe.frameBorder = "0";

    document.body.appendChild(modalInner);

    modalInner.appendChild(iframe);

    const closeButtonX = modalInner.querySelector(".js-c-button-close-modal");
    closeButtonX &&
      closeButtonX.addEventListener(
        "click",
        closeModalCallback({
          protectedModal,
          onCloseFunction
        })
      );

    openLightBox({ protectedModal, onCloseFunction, lightboxClasses });

    focusTarget = document.querySelector(`.${modalOuterClass} iframe`);
  } else if (!isNil(templates)) {
    logger.info(`${debugTag} showModal() | template mode`);

    modalInner.style.width = `${width}px`;
    modalInner.style.height = `${height}px`;

    document.body.appendChild(modalInner);

    loadNextTemplate(uid, templates);
    openLightBox({ protectedModal, onCloseFunction, lightboxClasses });

    ee.on("modal:template-next", loadNextTemplate);
  }

  modalOuterContainer = document.querySelector(`.${modalOuterClass}`);

  bootstrapFocusElements({
    targetElement: modalOuterContainer
  });

  toggleBgAccess(true);

  // delay to let the modal render
  setTimeout(() => {
    logger.info(`${debugTag} showModal() | timeout elapsed`);
    // if the iframe focusTarget is defined then focus that, otherwise focus on the first element within he modal
    focusOnElement(focusTarget || getFirstFocusableElement());
  }, 10);

  const modalUniqueId = uid;

  modalId += 1;

  modalRegister[modalUniqueId] = {
    uid: modalUniqueId,
    iid: modalId,
    iframeUrl: iframeUrl,
    sourceElement: sourceElement,
    onCloseModal: onCloseModal,
    element: modalInner
  };

  onOpenModal && onOpenModal();
  ee.trigger("modal:opened", [uid, templates]);

  return true;
}

/**
 * @method hideModal
 * @description destroys login iframe and reveals it
 * @param {Object} c - collection of properties
 * @param {HTMLElement} c.modalTarget - modal element to remove from the DOM
 * @param {String} c.sourceElement - a query selector string to an element you want to focus when the modal closes
 * @param {Function} [c.onCloseModal] - method to call when the modal closes
 */
function hideModal({ modalTarget, sourceElement, onCloseModal }) {
  modalTarget && document.body.removeChild(modalTarget);

  closeLightBox();
  toggleBgAccess(false);
  focusOnElement(document.querySelector(sourceElement));
  onCloseModal && onCloseModal();

  ee.trigger("modal:closed");
}

/**
 * @method hideAllModals
 * @description loops through all modals and deletes them
 */
export function hideAllModals() {
  if (Object.keys(modalRegister).length > 0) {
    qsa(`.${modalOuterClass}`).forEach(modalTarget => {
      const elementId = modalTarget.getAttribute("id");
      const { sourceElement, onCloseModal } = modalRegister[elementId];

      hideModal({ modalTarget, sourceElement, onCloseModal });
    });
  }
}

/**
 * @method loadNextTemplate
 * @description will change to the next template in the templates array
 */
function loadNextTemplate() {
  const [uid, templates] = arguments;

  if (templateIndex < templates.length) {
    logger.info(
      `${debugTag} loadNextTemplate() ${uid} template ${templateIndex + 1} of ${templates.length}`
    );
    const modalInner = document.querySelector(`.${modalOuterClass}`);

    modalInner.innerHTML = templates[templateIndex];
    templateIndex += 1;

    ee.trigger("modal:template-changed", [uid, templates]);
  } else {
    logger.info(`${debugTag} loadNextTemplate() | no more templates, close modal`);
    hideAllModals();
  }
}

/**
 * @method isUniqueId
 * @description if the uid does not exist in the register it will return true
 * @param {String} uidRef
 * @returns {Boolean}
 */
function isUniqueId(uidRef) {
  return Object.keys(modalRegister).every(key => {
    return modalRegister[key].uid !== uidRef;
  });
}
